import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import Layout from "./components/Layout";
import Dashboard from "./pages/Dashboard";
import Transactions from "./pages/Transactions";
import ReviewQueue from "./pages/ReviewQueue";
import Forecast from "./pages/Forecast";
import Nutrition from "./pages/Nutrition";
import Settings from "./pages/Settings";
import AgentStatus from "./pages/AgentStatus";
import Export from "./pages/Export";
import Login from "./pages/Login";
import ConnectedAccounts from "./pages/ConnectedAccounts";
import GmailCallback from "./pages/GmailCallback";
import GoogleAuthCallback from "./pages/GoogleAuthCallback";
import { ToastContainer } from "@xpense/ui";
import { api, type AuthMeResponse } from "./api/client";

const SESSION_STORAGE_KEY = "xpense_session_token";

function App() {
  const [sessionToken, setSessionToken] = useState<string | null>(() =>
    localStorage.getItem(SESSION_STORAGE_KEY)
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    if (!sessionToken) {
      api.setAuthToken(null);
      setLoading(false);
      return;
    }

    api.setAuthToken(sessionToken);
    setLoading(true);

    api
      .get<AuthMeResponse>("/api/v1/auth/me")
      .then(() => {
        if (active) {
          setLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          api.setAuthToken(null);
          localStorage.removeItem(SESSION_STORAGE_KEY);
          setSessionToken(null);
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [sessionToken]);

  const handleAuthenticated = (token: string) => {
    setSessionToken(token);
    localStorage.setItem(SESSION_STORAGE_KEY, token);
    api.setAuthToken(token);
  };

  const handleLogout = () => {
    setSessionToken(null);
    localStorage.removeItem(SESSION_STORAGE_KEY);
    api.setAuthToken(null);
  };

  return (
    <>
      <BrowserRouter>
        {loading ? (
          <div className="xpn-flex-center" style={{ minHeight: "100vh" }}>
            <div className="xpn-card" style={{ width: 260 }}>
              <div className="xpn-skeleton xpn-skeleton--title" />
              <div className="xpn-skeleton xpn-skeleton--text" />
            </div>
          </div>
        ) : !sessionToken ? (
          <Routes>
            <Route
              path="/auth/google/callback"
              element={<GoogleAuthCallback onAuthenticated={handleAuthenticated} />}
            />
            <Route path="*" element={<Login />} />
          </Routes>
        ) : (
          <Layout onLogout={handleLogout}>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/transactions" element={<Transactions />} />
              <Route path="/review" element={<ReviewQueue />} />
              <Route path="/forecast" element={<Forecast />} />
              <Route path="/nutrition" element={<Nutrition />} />
              <Route path="/agents" element={<AgentStatus />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/export" element={<Export />} />
              <Route path="/connected-accounts" element={<ConnectedAccounts />} />
              <Route path="/gmail-callback" element={<GmailCallback />} />
              <Route
                path="/auth/google/callback"
                element={<GoogleAuthCallback onAuthenticated={handleAuthenticated} />}
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Layout>
        )}
      </BrowserRouter>
      <ToastContainer />
    </>
  );
}

export default App;
